import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, FileText, Upload, FilePlus2, Download, FileCheck2, UserCheck, Loader2, MessageSquare, CircleDot, CheckCircle2, AlertCircle } from "lucide-react";
import { api, formatApiError, openDocument } from "../lib/api";

const DOC_KIND_LABELS = { identita: "Documento d'identità (fronte e retro)", tessera_sanitaria: "Tessera sanitaria" };

const STATUS_COLORS = {
  "Nuova": "bg-blue-100 text-blue-700",
  "In lavorazione": "bg-amber-100 text-amber-700",
  "In attesa cliente": "bg-orange-100 text-orange-700",
  "In attesa reparto": "bg-purple-100 text-purple-700",
  "Completata": "bg-emerald-100 text-emerald-700",
  "Chiusa": "bg-slate-200 text-slate-600",
};
const TYPE_LABELS = { utenze: "Utenze", credito: "Credito", assicurazioni: "Assicurazioni", controllo_crif: "Controllo CRIF", controllo_banche_dati: "Banche Dati", assistenza_stradale: "Assistenza Stradale" };
const TYPE_COLORS = { utenze: "#10B981", credito: "#0A192F", assicurazioni: "#F97316", controllo_crif: "#0A192F", controllo_banche_dati: "#0A192F", assistenza_stradale: "#10B981" };

const EVENT_ICONS = {
  created: FilePlus2,
  document: FileCheck2,
  assigned: UserCheck,
  status: Loader2,
  reply: MessageSquare,
};

const fmtDateTime = (iso) => new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

export default function PraticaDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [req, setReq] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [pendingKind, setPendingKind] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    api.get(`/requests/${id}`).then((r) => setReq(r.data)).catch(() => setReq(false));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const triggerUpload = (kind) => { setPendingKind(kind || null); fileRef.current?.click(); };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    const kind = pendingKind;
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (!["pdf", "jpg", "jpeg", "png"].includes(ext)) {
      toast.error("Formato non supportato. Usa PDF, JPG o PNG.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (kind) fd.append("doc_kind", kind);
      await api.post(`/requests/${id}/documents`, fd);
      toast.success("Documento caricato");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Caricamento non riuscito");
    } finally {
      setUploading(false);
      setPendingKind(null);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const download = async (docId) => {
    try { await openDocument(docId); } catch { toast.error("Download non riuscito"); }
  };

  if (req === null) return <div className="py-20 text-center text-slate-400">Caricamento...</div>;
  if (req === false) return <div className="py-20 text-center text-slate-500">Pratica non trovata.</div>;

  const events = [...(req.events || [])].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  const requiredDocs = req.required_docs || null;
  const presentKinds = new Set((req.documents || []).map((d) => d.doc_kind).filter(Boolean));
  const missingCount = requiredDocs ? requiredDocs.filter((k) => !presentKinds.has(k)).length : 0;

  return (
    <div data-testid="pratica-detail-page" className="space-y-6">
      <button data-testid="pratica-detail-back" onClick={() => navigate("/area/pratiche")} className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-[#0A192F]">
        <ArrowLeft className="h-4 w-4" /> Torna alle pratiche
      </button>

      <div className="flex flex-wrap items-center gap-3">
        <span className="rounded-full px-3 py-1 text-xs font-bold text-white" style={{ backgroundColor: TYPE_COLORS[req.type] }}>{TYPE_LABELS[req.type]}</span>
        <h1 className="font-display text-2xl font-bold text-[#0A192F]">{req.title}</h1>
        <span className={`rounded-full px-3 py-1 text-xs font-semibold ${STATUS_COLORS[req.status] || "bg-slate-100 text-slate-600"}`}>{req.status}</span>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Timeline */}
        <div className="lg:col-span-2 space-y-6">
          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <h3 className="font-display text-lg font-bold text-[#0A192F]">Cronologia</h3>
            <div className="mt-6">
              {events.length === 0 ? (
                <p className="text-sm text-slate-400">Nessun evento.</p>
              ) : (
                <ol className="relative border-l-2 border-slate-100">
                  {events.map((ev) => {
                    const Icon = EVENT_ICONS[ev.type] || CircleDot;
                    return (
                      <li key={ev.id} data-testid={`pratica-event-${ev.id}`} className="mb-6 ml-6 last:mb-0">
                        <span className="absolute -left-[13px] flex h-6 w-6 items-center justify-center rounded-full bg-[#10B981] ring-4 ring-white">
                          <Icon className="h-3.5 w-3.5 text-white" />
                        </span>
                        <p className="font-semibold text-[#0A192F]">{ev.label}</p>
                        {ev.description && <p className="text-sm text-slate-600">{ev.description}</p>}
                        <p className="mt-0.5 text-xs text-slate-400">{fmtDateTime(ev.created_at)}</p>
                      </li>
                    );
                  })}
                </ol>
              )}
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <h3 className="font-display text-lg font-bold text-[#0A192F]">Descrizione</h3>
            <p className="mt-2 text-slate-700">{req.description || <span className="text-slate-400">Nessuna descrizione</span>}</p>
          </div>
        </div>

        {/* Documents + upload */}
        <div className="space-y-6">
          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <h3 className="font-display text-lg font-bold text-[#0A192F]">Documenti</h3>
            <p className="mt-1 text-xs text-slate-500">Formati: PDF, JPG, PNG (max 10MB).</p>
            <input ref={fileRef} data-testid="pratica-file-input" type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={onUpload} className="hidden" />

            {requiredDocs ? (
              <div data-testid="pratica-required-docs" className="mt-4 space-y-3">
                {missingCount > 0 ? (
                  <div className="rounded-xl border border-[#F97316]/30 bg-[#F97316]/5 p-3 text-sm text-[#0A192F]">
                    Per avviare la verifica allega i documenti richiesti. Mancano ancora {missingCount} document{missingCount === 1 ? "o" : "i"}.
                  </div>
                ) : (
                  <div className="rounded-xl border border-[#10B981]/30 bg-[#10B981]/5 p-3 text-sm text-[#0A192F]">
                    Tutti i documenti richiesti sono stati caricati. La pratica è in verifica.
                  </div>
                )}
                {requiredDocs.map((k) => {
                  const done = presentKinds.has(k);
                  return (
                    <div key={k} data-testid={`pratica-docslot-${k}`} className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 p-3">
                      <div className="flex items-center gap-2 text-sm">
                        {done ? <CheckCircle2 className="h-5 w-5 flex-shrink-0 text-[#10B981]" /> : <AlertCircle className="h-5 w-5 flex-shrink-0 text-[#F97316]" />}
                        <span className={`font-medium ${done ? "text-[#0A192F]" : "text-slate-600"}`}>{DOC_KIND_LABELS[k] || k}</span>
                      </div>
                      <button data-testid={`pratica-docslot-upload-${k}`} onClick={() => triggerUpload(k)} disabled={uploading} className="flex-shrink-0 rounded-full bg-[#10B981] px-4 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-[#059669] disabled:opacity-60">
                        {done ? "Sostituisci" : "Carica"}
                      </button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <button data-testid="pratica-upload-btn" onClick={() => triggerUpload(null)} disabled={uploading} className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-[#10B981] px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#059669] disabled:opacity-60">
                <Upload className="h-4 w-4" /> {uploading ? "Caricamento..." : "Carica documento"}
              </button>
            )}

            <div className="mt-4 space-y-3">
              {(req.documents || []).length === 0 ? (
                <p className="text-sm text-slate-400">Nessun documento caricato.</p>
              ) : (
                req.documents.map((d) => (
                  <button key={d.id} data-testid={`pratica-doc-${d.id}`} onClick={() => download(d.id)} className="flex w-full items-center gap-3 rounded-xl border border-slate-200 p-3 text-left transition-colors hover:border-[#10B981]">
                    <FileText className="h-5 w-5 flex-shrink-0 text-[#10B981]" />
                    <div className="flex-1 overflow-hidden">
                      <p className="truncate font-semibold text-[#0A192F]">{d.name}</p>
                      <p className="text-xs text-slate-400">{d.doc_kind ? `${DOC_KIND_LABELS[d.doc_kind] || d.doc_kind} · ` : ""}{fmtDateTime(d.created_at)}</p>
                    </div>
                    <Download className="h-4 w-4 text-slate-400" />
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
